/* eslint-disable no-loop-func */
/* eslint-disable react-hooks/exhaustive-deps */
import topDeck from "@assets/images/aircraft/business.svg";
import nose from "@assets/images/aircraft/nose.svg";
import tail from "@assets/images/aircraft/tail.svg";
import { AircraftType } from "@common/constans";
import { CabinClass, ITraveler, SeatMapItemType, SeatStatus } from "@common/models";
import Icon from "@components/general/Icon";
import { IViewport } from "@config/appConfig";
import { AppAction, IAppAction } from "@misc/Action";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import Translate from "@service/Translate";
import { toPascalCase } from "@utilize/Format";
import { FC, Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Seat from "./Seat";

interface ICheckInProps {
  miniMode?: boolean;
}

const SeatMap: FC<ICheckInProps> = ({ miniMode }) => {
  const { tr } = Translate;
  const dispatch = useDispatch();
  const seatSelectedMiniModeRef = useRef<HTMLDivElement>(null);
  const { small, large } = Config.get<IViewport>("viewport");
  const { airCheckinInfo, seatMap, airCheckinSelectedTravelers, seatMapInitData, width } = useSelector((state: IAppState) => ({
    airCheckinInfo: state.airCheckinInfo,
    airCheckinRequest: state.airCheckinRequest,
    airCheckinSelectedTravelers: state.airCheckinSelectedTravelers,
    seatMap: state.seatMap,
    width: state.windowWidth,
    seatMapInitData: state.seatMapInitData,
    seatMapRegister: state.seatMapRegister
  }));
  // TODO: set seat loading on api call
  const [disableMode, setDisableMode] = useState<boolean>(true);
  const businessSeat: ReactNode[] = [];
  const premiumEconomySeat: ReactNode[] = [];
  const economySeat: ReactNode[] = [];
  const classOfService = airCheckinInfo.originDestination?.classOfService;
  const aircraftType = airCheckinInfo.originDestination?.flightSegments[0].equipmentModelInfo || '';
  const sortedSeatMap = seatMap?.items?.sort((a, b) => a.rowId - b.rowId || a.columnId - b.columnId) || [];
  const filteredSeatMap = width <= small && !miniMode ? sortedSeatMap.filter((s) => s.classOfService === classOfService) : sortedSeatMap;
  const lastRowId = filteredSeatMap.length ? filteredSeatMap[filteredSeatMap.length - 1].rowId : 0;
  const wingPosition = { topIndex: 0, bottomIndex: 0 };
  const selectedPassengerIndex = airCheckinSelectedTravelers?.findIndex((p) => p.isSelected) || 0;
  const animDuration = 1000;
  const hasTopDeck = aircraftType === AircraftType.B743 || aircraftType === AircraftType.B744;
  const activeTopDeck = hasTopDeck && classOfService === CabinClass.C;

  useEffect(() => {
    if (width >= small) {
      const element = document.querySelector(".seat-map-body") as Element;
      dispatch<IAppAction>({ type: AppAction.CreateRealPoint, payload: { seatMapRegister: { element, type: aircraftType, register: "" } } });
    }
  }, [width]);

  useEffect(() => {
    const index = airCheckinSelectedTravelers?.findIndex((p) => p.isSelected);
    if (index !== -1) setDisableMode(false);
    else setDisableMode(true);
  }, [airCheckinSelectedTravelers]);

  useEffect(() => {
    setTimeout(() => {
      if (seatSelectedMiniModeRef.current) {
        const seatSelectedMiniModeElement = seatSelectedMiniModeRef.current;
        seatSelectedMiniModeElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    }, 500);
  }, [seatSelectedMiniModeRef])

  const reserveSeatNumber = (selectSeatNumber: string, traveler: ITraveler) => {
    animateSeat(selectSeatNumber);
    setTimeout(() => {
      setDisableMode(false);
      if (traveler.tickets?.length)
        traveler.tickets[0].seatNumber = selectSeatNumber;
      traveler.isSelected = false;
      const nextTraveler = airCheckinSelectedTravelers && airCheckinSelectedTravelers[selectedPassengerIndex + 1] ?
        airCheckinSelectedTravelers[selectedPassengerIndex + 1] : undefined;
      if (width > small) {
        if (nextTraveler && (nextTraveler.tickets && !nextTraveler.tickets[0].seatNumber))
          nextTraveler.isSelected = true;
      } else {
        if (nextTraveler && (nextTraveler.tickets && !nextTraveler.tickets[0].seatNumber))
          nextTraveler.isSelected = true;
        else
          document.getElementById("selectSeatPassengersInfo")?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
      }
      const aircheckinInfoTraveler = airCheckinSelectedTravelers?.find((t) => t.paxNo === traveler.paxNo);
      if (aircheckinInfoTraveler?.tickets)
        aircheckinInfoTraveler.tickets[0].seatNumber = traveler.tickets && traveler.tickets[0].seatNumber;
      dispatch<IAppAction>({ type: AppAction.ManageSeat, payload: { manageSelectPassenger: 1 } });
      dispatch<IAppAction>({ type: AppAction.AirCheckinSelectedTravelers, payload: { airCheckinSelectedTravelers: JSON.parse(JSON.stringify(airCheckinSelectedTravelers)) } });
    }, animDuration);
  };

  const modifySeatNumber = (selectSeatNumber: string, traveler: ITraveler) => {
    animateSeat(selectSeatNumber);
    setTimeout(() => {
      setDisableMode(false);
      traveler.isSelected = false;
      const releasedSeat = seatMap.items?.find(i => i.seatNumber === (traveler?.tickets?.length && traveler?.tickets[0].seatNumber));
      if (traveler.tickets?.length)
        traveler.tickets[0].seatNumber = selectSeatNumber;
      if (releasedSeat)
        releasedSeat.seatStatus = SeatStatus.Free;
      dispatch<IAppAction>({ type: AppAction.AirCheckinSelectedTravelers, payload: { airCheckinSelectedTravelers: JSON.parse(JSON.stringify(airCheckinSelectedTravelers)) } });
      dispatch<IAppAction>({ type: AppAction.SeatMap, payload: { seatMap } });
      document.getElementById("selectSeatPassengersInfo")?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    }, animDuration);
  };

  const animateSeat = (selectSeatNumber: string) => {
    if (airCheckinSelectedTravelers) {
      setDisableMode(true);
      const traveler = airCheckinSelectedTravelers[selectedPassengerIndex];
      const seatParentElement = document.getElementById(selectSeatNumber);
      const seat = seatParentElement?.firstChild as HTMLElement;
      const seatClone = seat.cloneNode(true) as HTMLElement;
      const seatBindingRect = seat.getBoundingClientRect();
      const { top: seatTop, right: seatRight, left: seatLeft } = seatBindingRect;
      seatParentElement?.appendChild(seatClone);
      const id = width <= small ? "passengerSeatSelect" : traveler.refNumber || "";
      const seatTarget = document.getElementById(id);
      const seatTargetBindingRect = seatTarget?.getBoundingClientRect() as DOMRect;
      const { top: seatTargetTop, right: seatTargetRight } = seatTargetBindingRect;
      const transform = seatParentElement?.parentElement?.parentElement?.style.transform;
      if (seatParentElement?.parentElement?.parentElement)
        seatParentElement.parentElement.parentElement.style.transform = '';
      seatClone.id = "cloneSeat";
      seatClone.classList.remove("free");
      seatClone.classList.add("selected");
      seatClone.style.height = "30px";
      seatClone.style.width = "40px";
      seatClone.style.position = "absolute";
      seatClone.style.zIndex = "20";
      seatClone.style.opacity = "0.7";
      const element = document.querySelector('body') as HTMLBodyElement;
      const direction = window.getComputedStyle(element).direction;
      if (width <= large) {
        dispatch<IAppAction>({ type: AppAction.ManageSeat, payload: { manageSelectPassenger: 2 } })
        if (direction === 'rtl') {
          seatClone.style.transform = `translate(${(width - seatLeft - 54)}px,${(seatTop - 9) * -1}px)`;
          seatClone.style.width = '40px'
          seatClone.style.height = '36px'
        }
        else {
          seatClone.style.transform = `translate(${-(seatLeft - 16)}px,${(seatTop - 9) * -1}px)`;
          seatClone.style.width = '40px'
          seatClone.style.height = '36px'
        }
      } else {
        seatClone.style.transform = `translate(${seatTargetRight - seatRight - 120}px,${seatTargetTop - seatTop + 13}px)`;
      }
      seatClone.style.transition = `transform ${animDuration / 1000}s ease-out`;
      setTimeout(() => {
        seatParentElement?.removeChild(seatClone);
        if (seatParentElement?.parentElement?.parentElement)
          seatParentElement.parentElement.parentElement.style.transform = transform || '';
      }, animDuration);
    }
  };

  const acceptSeatNumber = (selectSeatNumber: string) => {
    if (airCheckinSelectedTravelers) {
      const traveler = airCheckinSelectedTravelers[selectedPassengerIndex];
      if (traveler?.tickets?.length && traveler.tickets[0].seatNumber)
        modifySeatNumber(selectSeatNumber, traveler);
      else
        reserveSeatNumber(selectSeatNumber, traveler);
    }
  };

  //svg
  const [p1xl, p1yl] = seatMapInitData.SVGPoint.pointStart ? seatMapInitData.SVGPoint.pointStart : [0, 0];
  const [p1x, p1y] = seatMapInitData.SVGPoint.point1 ? seatMapInitData.SVGPoint.point1 : [0, 0];
  const [p2x, p2y] = seatMapInitData.SVGPoint.point2 ? seatMapInitData.SVGPoint.point2 : [0, 0];
  const [bx, by] = seatMapInitData.SVGPoint.realBizierPoint ? seatMapInitData.SVGPoint.realBizierPoint : [0, 0];
  const [avex, avey] = seatMapInitData.SVGPoint.avragePoint ? seatMapInitData.SVGPoint.avragePoint : [0, 0];
  const [pr2x, pr2y] = seatMapInitData.SVGPoint.revertPoint2 ? seatMapInitData.SVGPoint.revertPoint2 : [0, 0];
  const [rbx, rby] = seatMapInitData.SVGPoint.revertRealBizierPoint ? seatMapInitData.SVGPoint.revertRealBizierPoint : [0, 0];
  const [pr1x, pr1y] = seatMapInitData.SVGPoint.revertPoint1 ? seatMapInitData.SVGPoint.revertPoint1 : [0, 0];
  const [rpe1xl, rpe1yly] = seatMapInitData.SVGPoint.pointEnd ? seatMapInitData.SVGPoint.pointEnd : [0, 0];
  const [nosePointStartX, nosePointStartY] = seatMapInitData.noseData ? seatMapInitData.noseData.data[0] : [0, 0];
  const [nosePointEndX, nosePointEndY] = seatMapInitData.noseData ? seatMapInitData.noseData.data[1] : [0, 0];
  const [noseBizierX, noseBizierY] = seatMapInitData.noseData ? seatMapInitData.noseData.data[2] : [0, 0];
  const [rnosePointStartX, rnosePointStartY] = seatMapInitData.noseData ? seatMapInitData.noseData.revert[0] : [0, 0];
  const [rnosePointEndX, rnosePointEndY] = seatMapInitData.noseData ? seatMapInitData.noseData.revert[1] : [0, 0];
  const [rnoseBizierX, rnoseBizierY] = seatMapInitData.noseData ? seatMapInitData.noseData.revert[2] : [0, 0];
  const draft = `M${p1xl},${p1yl} L${p1x},${p1y} Q${bx},${by} ${p2x},${p2y} Q${avex},${avey} ${pr2x},${pr2y} Q${rbx},${rby} ${pr1x},${pr1y} L${rpe1xl},${rpe1yly}`;
  const draft2 = `M${nosePointStartX},${nosePointStartY} Q${noseBizierX},${noseBizierY} ${nosePointEndX},${nosePointEndY} L${p1x},${p1y} Q${bx},${by} ${p2x},${p2y} Q${avex},${avey} ${pr2x},${pr2y} Q${rbx},${rby} ${pr1x},${pr1y} L${rnosePointEndX},${rnosePointEndY} Q${rnoseBizierX},${rnoseBizierY} ${rnosePointStartX},${rnosePointStartY}`;

  let id: number = 0;
  let rowIndex: number = 1;
  let shouldHaveEnterFlag: boolean = true;
  for (let i = 1; i <= lastRowId; i++) {
    let cols: ReactNode[] = [];
    const group: ReactNode[] = [];
    const seats = filteredSeatMap.filter((sm) => sm.rowId === i);
    if (seats.length) {
      const setLowWidthSeat = (seats.length >= 12) ? ' set-low-width-seat' : '';
      const setNonePadding = (seats.length >= 12) ? 'set-none-padding' : 'seat-col';
      let wingExist = false;
      seats.forEach((seat, index) => {
        const classList = seat.positionCode?.split(",");
        const traveler = airCheckinSelectedTravelers?.find((t) => seat.seatNumber && (t.tickets && t.tickets[0].seatNumber) === seat.seatNumber);
        let formatedClassList = "";
        classList?.forEach((cl) => {
          let positionCode = cl.replace("[", "").replace("]", "");
          formatedClassList = formatedClassList + " " + positionCode + setLowWidthSeat;
        });
        if (seat.wing)
          wingExist = true;
        const exitRow = seat.itemType === SeatMapItemType.EXR && seat.positionCode === "[E]";
        const col = (
          <div key={index} className={`col-wrapper${traveler ? " selected" : seat.seatStatus === SeatStatus.Free ? " free" : " reserved"}`}>
            <div
              id={seat.seatNumber}
              className={`${setNonePadding} class-${CabinClass[seat.classOfService!].toLowerCase()} type-${SeatMapItemType[seat.itemType].toLowerCase()}${exitRow ? " exit" : ""}${shouldHaveEnterFlag && exitRow ? " enter" : ""}`}
              onClick={() => seat.classOfService !== classOfService ? "" : acceptSeatNumber(seat.seatNumber!)}
            >
              {
                seat.seatNumber && seat.seatStatus !== undefined ?
                  miniMode ?
                    <div ref={traveler && seatSelectedMiniModeRef} className={`minimal-seat${traveler ? " selected" : ""}`} />
                    :
                    <Seat
                      size="auto"
                      status={traveler ? "selected" : seat.seatStatus === SeatStatus.Free ? "free" : "reserved"}
                      className={formatedClassList}
                    >
                      {seat.hasInfant ? <Icon name="icon-infant" /> : traveler ? toPascalCase(traveler.firstName?.replace("Seyed", "").replace("SEYED", "").substring(0, 3) || "") : seat.seatNumber}
                    </Seat>
                  : ""
              }
            </div>
          </div>
        );

        if (shouldHaveEnterFlag && exitRow)
          shouldHaveEnterFlag = false;

        if (seat.itemType === SeatMapItemType.Aisle) {
          group.push(<li key={index} className="group-seat" style={{ width: `${(cols.length * 100) / seats.length}%` }}>{cols}</li>);
          group.push(<li key={index + 1} className="aisle">{col}</li>);
          cols = [];
        } else {
          cols.push(col);
        }
      });
      group.push(<li key={group.length} className="group-seat" style={{ width: `${(cols.length * 100) / seats.length}%` }}>{cols}</li>);
      id = seats[0].seatNumber ? +seats[0].seatNumber?.slice(0, seats[0].seatNumber.length - 1) : id ? id + 1 : 0;
      const row = <ul className="seat-col-container">{group}</ul>;
      const rowItem = (
        <li key={i} id={id.toString()} className="seat-row" >
          {row}
        </li>
      );
      if (seats[0].classOfService === CabinClass.A)
        premiumEconomySeat.push(rowItem);
      else if (seats[0].classOfService === CabinClass.C)
        businessSeat.push(rowItem);
      else if (seats[0].classOfService === CabinClass.Y)
        economySeat.push(rowItem);
      // set position of wing base on WG flag
      if (wingExist && !wingPosition.topIndex)
        wingPosition.topIndex = rowIndex;
      else if (!wingExist && wingPosition.topIndex && !wingPosition.bottomIndex)
        wingPosition.bottomIndex = rowIndex;
      rowIndex++;
    }
  }

  const wingStyle = {
    top: (wingPosition.topIndex - 1) * (miniMode ? 10 : 42) + 10,
    bottom: (rowIndex - wingPosition.bottomIndex) * (miniMode ? 10 : 42) - 10,
  };

  return (
    <div id="seatMap" className={`seat-map en${disableMode ? " deactive" : " active"}${miniMode ? " mini-mode" : ""}${activeTopDeck ? " active-top-deck" : ""}`}>
      {
        activeTopDeck && miniMode ?
          <div id="seatMapBody" className="seat-map-body">
            <span className="seat-title">{tr("cabin_class_c")}</span>
            {
              businessSeat.length ?
                <ul className={`seat-map-container business-seat${classOfService !== CabinClass.C ? " deactive-seat" : ""}`}>
                  {businessSeat.map((row, index) => <Fragment key={index}>{row}</Fragment>)}
                </ul> : ""
            }
          </div>
          :
          activeTopDeck && width > small ?
            <div className="top-deck">
              <img className="top-deck-background" src={topDeck} alt="top deck" />
              <ul className={`seat-map-container business-seat${classOfService !== CabinClass.C ? " deactive-seat" : ""}`}>
                {businessSeat.map((row, index) => <Fragment key={index}>{row}</Fragment>)}
              </ul>
            </div>
            :
            <Fragment>
              <div className="seat-map-nose">
                <img src={nose} alt="nose of aircraft" />
              </div>
              <div id="seatMapBody" className="seat-map-body">
                <svg id="svg" className="seat-map-svg">
                  <path d={seatMapInitData?.noseData ? draft2 : draft}></path>
                </svg>
                <div className="seat-map-wing left-wing" style={wingStyle}>
                  <div className="wing-flap wing-flap-left wing-flap-top" />
                  <div className="wing-flap wing-flap-left wing-flap-bottom" />
                </div>
                {
                  businessSeat.length ?
                    <ul id="businessSeat" className={`seat-map-container business-seat${classOfService !== CabinClass.C ? " deactive-seat" : ""}`}>
                      <span className="seat-title">{tr("cabin_class_c")}</span>
                      {businessSeat.map((row, index) => <Fragment key={index}>{row}</Fragment>)}
                    </ul> : ""
                }
                {
                  premiumEconomySeat.length ?
                    <ul id="premiumEconomySeat" className={`seat-map-container premium-economy-seat${classOfService !== CabinClass.A ? " deactive-seat" : ""}`}>
                      <span className="seat-title">{tr("cabin_class_a")}</span>
                      {premiumEconomySeat.map((row, index) => <Fragment key={index}>{row}</Fragment>)}
                    </ul> : ""
                }
                {
                  economySeat.length ?
                    <ul id="economySeat" className={`seat-map-container economy-seat${classOfService !== CabinClass.Y ? " deactive-seat" : ""}`}>
                      <span className="seat-title">{tr("cabin_class_y")}</span>
                      {economySeat.map((row, index) => <Fragment key={index}>{row}</Fragment>)}
                    </ul> : ""
                }
                <div className="seat-map-wing right-wing" style={wingStyle}>
                  <div className="wing-flap wing-flap-right wing-flap-top" />
                  <div className="wing-flap wing-flap-right wing-flap-bottom" />
                </div>
              </div>
              <div className="seat-map-tail">
                <img src={tail} alt="tail of aircraft" />
              </div>
            </Fragment>
      }
    </div>
  );
};

export default SeatMap;
